import { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { loadLipSyncConfig } from '../config/lipSyncDefaults';

const __connectedAudioElements = new WeakMap();

function clamp(value, min = 0, max = 1) {
  return Math.min(Math.max(value, min), max);
}

function lerp(a, b, t) {
  return a + (b - a) * clamp(t);
}

function normalizeName(name = '') {
  return String(name).toLowerCase().replace(/[^a-z0-9]/g, '');
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function getCandidateNames(key, mapping = {}) {
  const aliases = Array.isArray(mapping.aliases) ? mapping.aliases : [];
  const targets = Array.isArray(mapping.targets) ? mapping.targets : [];
  return unique([key, mapping.name, mapping.bone, mapping.boneName, ...aliases, ...targets]);
}

function estimateSpeechCue(text, audioEl, playing) {
  if (!playing || !text || !audioEl || !Number.isFinite(audioEl.duration) || audioEl.duration <= 0) {
    return { morphs: {}, bones: {} };
  }

  const clean = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ');

  if (!clean.trim()) return { morphs: {}, bones: {} };

  const progress = clamp(audioEl.currentTime / audioEl.duration);
  const index = clamp(Math.floor(progress * clean.length), 0, Math.max(clean.length - 1, 0));
  const windowText = clean.slice(Math.max(0, index - 2), index + 3);

  const has = pattern => pattern.test(windowText);
  const open = has(/[a]/) ? 0.95 : has(/[eo]/) ? 0.62 : has(/[iu]/) ? 0.38 : 0.18;
  const rounded = has(/[ou]/) ? 1 : 0;
  const wide = has(/[eiyszx]/) ? 1 : 0;
  const bilabial = has(/[bmp]/) ? 1 : 0;
  const dental = has(/[fv]/) ? 1 : 0;
  const tongue = has(/[tdnlr]/) ? 1 : 0;

  return {
    morphs: {
      jawOpen: open,
      mouthClose: bilabial * 0.55,
      mouthFunnel: rounded * 0.75,
      mouthPucker: rounded,
      mouthStretch_L: wide * 0.75,
      mouthStretch_R: wide * 0.75,
      mouthPress_L: bilabial * 0.45,
      mouthPress_R: bilabial * 0.45,
      mouthLowerDown_L: clamp(open * 0.42 + dental * 0.18),
      mouthLowerDown_R: clamp(open * 0.42 + dental * 0.18),
      mouthUpperUp_L: clamp(open * 0.24 + wide * 0.18),
      mouthUpperUp_R: clamp(open * 0.24 + wide * 0.18),
      tongue_out: tongue * 0.12,
    },
    bones: {
      jaw: open * 0.45,
      jaw_open: open,
      jaw_down: open * 0.7,
      mouth_funnel: rounded,
      mouth_pucker: rounded,
      mouth_stretch_L: wide,
      mouth_stretch_R: wide,
      mouth_upper_up_L: clamp(open * 0.35 + wide * 0.25),
      mouth_upper_up_R: clamp(open * 0.35 + wide * 0.25),
      mouth_lower_down_L: open,
      mouth_lower_down_R: open,
      mouth_press_L: bilabial,
      mouth_press_R: bilabial,
      tongue: tongue,
    },
  };
}

function mappingSignal(mapping, bands, textValue, fallbackTextGain, maxValue = 1) {
  const signal =
    bands.low * (mapping.low || 0) +
    bands.mid * (mapping.mid || 0) +
    bands.high * (mapping.high || 0) +
    bands.volume * (mapping.volume || 0) +
    (textValue || 0) * (mapping.text ?? fallbackTextGain ?? 0);

  return clamp(signal, 0, mapping.max ?? maxValue);
}

// Audio-reactive lip sync with cached morph meshes, exact bone aliases and text cues.
function LipSyncController({ audioPlayerRef, audioRef, avatarRef, playing, previewValue, speechText }) {
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const frequencyDataRef = useRef(null);
  const timeDataRef = useRef(null);
  const lastAudioElRef = useRef(null);

  const configRef = useRef(loadLipSyncConfig());
  const smoothBandsRef = useRef({ low: 0, mid: 0, high: 0, volume: 0 });
  const smoothMorphsRef = useRef({});
  const morphMeshesRef = useRef([]);
  const boneCacheRef = useRef({});

  const getAudioEl = () => {
    if (audioRef?.current) return audioRef.current;
    if (audioPlayerRef?.current?.audioEl?.current) return audioPlayerRef.current.audioEl.current;
    return null;
  };

  function cacheAvatarTargets(cfg = configRef.current) {
    if (!avatarRef) return;

    const morphMeshes = [];
    const bones = [];

    avatarRef.traverse(node => {
      if (node.morphTargetInfluences && node.morphTargetDictionary) {
        morphMeshes.push(node);
      }

      if (node.isBone) {
        bones.push({
          node,
          name: node.name || '',
          normalized: normalizeName(node.name || ''),
        });
      }
    });

    const nextBoneCache = {};
    Object.entries(cfg.bones || {}).forEach(([key, mapping]) => {
      const candidates = getCandidateNames(key, mapping).map(normalizeName);
      let matches = bones.filter(bone => candidates.includes(bone.normalized));

      if (matches.length === 0) {
        const fuzzy = bones.filter(bone =>
          candidates.some(candidate => candidate && bone.normalized.includes(candidate))
        );
        matches = mapping.multi ? fuzzy : fuzzy.slice(0, 1);
      }

      nextBoneCache[key] = matches.map(match => {
        // Preserve existing base transforms so we don't drift on re-cache
        const existing = boneCacheRef.current[key]?.find(item => item.bone === match.node);
        return {
          bone: match.node,
          baseRotX: existing?.baseRotX ?? match.node.rotation.x,
          baseRotY: existing?.baseRotY ?? match.node.rotation.y,
          baseRotZ: existing?.baseRotZ ?? match.node.rotation.z,
          basePosX: existing?.basePosX ?? match.node.position.x,
          basePosY: existing?.basePosY ?? match.node.position.y,
          basePosZ: existing?.basePosZ ?? match.node.position.z,
          smoothedIntensity: existing?.smoothedIntensity || 0,
        };
      });
    });

    morphMeshesRef.current = morphMeshes;
    boneCacheRef.current = nextBoneCache;
  }

  useEffect(() => {
    configRef.current = loadLipSyncConfig();
    cacheAvatarTargets(configRef.current);
    const interval = setInterval(() => {
      configRef.current = loadLipSyncConfig();
      cacheAvatarTargets(configRef.current);
    }, 500);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarRef]);

  useEffect(() => {
    const checkAndConnect = () => {
      const audioEl = getAudioEl();
      if (!audioEl) return;
      if (audioEl === lastAudioElRef.current) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const audioCtx = audioContextRef.current;
      if (!analyserRef.current) {
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.55;
        analyserRef.current = analyser;
        frequencyDataRef.current = new Uint8Array(analyser.frequencyBinCount);
        timeDataRef.current = new Uint8Array(analyser.fftSize);
      }

      if (!__connectedAudioElements.has(audioEl)) {
        try {
          const source = audioCtx.createMediaElementSource(audioEl);
          source.connect(analyserRef.current);
          analyserRef.current.connect(audioCtx.destination);
          __connectedAudioElements.set(audioEl, true);
        } catch (e) {
          // The element may already be connected by another visualizer.
        }
      }

      lastAudioElRef.current = audioEl;
    };

    checkAndConnect();
    const interval = setInterval(checkAndConnect, 300);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioPlayerRef, audioRef]);

  useEffect(() => {
    const resumeAudio = () => {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
    };

    window.addEventListener('click', resumeAudio);
    window.addEventListener('touchstart', resumeAudio);
    return () => {
      window.removeEventListener('click', resumeAudio);
      window.removeEventListener('touchstart', resumeAudio);
    };
  }, []);

  useFrame(() => {
    if (!avatarRef) return;

    const analyser = analyserRef.current;
    const frequencyData = frequencyDataRef.current || new Uint8Array(128);
    const timeData = timeDataRef.current || new Uint8Array(256).fill(128);
    const cfg = configRef.current;
    const bands = cfg.bands || {};
    const global = cfg.global || {};
    const audioEl = getAudioEl();

    analyser?.getByteFrequencyData(frequencyData);
    analyser?.getByteTimeDomainData(timeData);
    const active = playing || (previewValue !== undefined && previewValue !== null);

    const bandAverage = (startBin = 0, endBin = 0) => {
      let sum = 0;
      let count = 0;
      const start = Math.max(0, Math.floor(startBin));
      const end = Math.min(frequencyData.length, Math.floor(endBin));
      for (let i = start; i < end; i++) {
        sum += frequencyData[i];
        count++;
      }
      return count > 0 ? sum / count : 0;
    };

    let rms = 0;
    for (let i = 0; i < timeData.length; i++) {
      const centered = (timeData[i] - 128) / 128;
      rms += centered * centered;
    }
    rms = Math.sqrt(rms / timeData.length);

    const normalize = value => {
      const floor = global.noiseFloor ?? 4;
      const divisor = Math.max((global.normalizeDivisor ?? 128) - floor, 1);
      const normalized = clamp((value - floor) / divisor);
      return playing ? Math.pow(normalized, global.powerCurve ?? 1.35) : 0;
    };

    const rawLow = bandAverage(bands.low?.startBin, bands.low?.endBin);
    const rawMid = bandAverage(bands.mid?.startBin, bands.mid?.endBin);
    const rawHigh = bandAverage(bands.high?.startBin, bands.high?.endBin);
    const rawVolume = rms * 255;

    const targetBands = previewValue !== undefined && previewValue !== null
      ? { low: previewValue, mid: previewValue, high: previewValue, volume: previewValue }
      : {
          low: normalize(rawLow),
          mid: normalize(rawMid),
          high: normalize(rawHigh),
          volume: normalize(rawVolume),
        };

    const smoothed = smoothBandsRef.current;
    Object.entries(targetBands).forEach(([key, target]) => {
      const isRising = target > smoothed[key];
      const speed = playing
        ? (isRising ? global.attack ?? 0.24 : global.release ?? 0.16)
        : 0.28;
      smoothed[key] = lerp(smoothed[key] || 0, target, speed);
    });

    const speechCue = estimateSpeechCue(speechText, audioEl, playing);
    const smoothMorphs = smoothMorphsRef.current;

    Object.entries(cfg.morphTargets || {}).forEach(([key, mapping]) => {
      const target = active
        ? mappingSignal(mapping, smoothed, speechCue.morphs[key], global.textInfluence, 1)
        : 0;
      const factor = active ? (mapping.lerp ?? global.smoothing ?? 0.14) : 0.28;
      smoothMorphs[key] = lerp(smoothMorphs[key] || 0, target, factor);
    });

    morphMeshesRef.current.forEach(mesh => {
      const dict = mesh.morphTargetDictionary;
      const inf = mesh.morphTargetInfluences;
      if (!dict || !inf) return;

      Object.entries(smoothMorphs).forEach(([key, value]) => {
        if (dict[key] !== undefined) {
          inf[dict[key]] = value;
        }
      });
    });

    Object.entries(cfg.bones || {}).forEach(([key, mapping]) => {
      const targets = boneCacheRef.current[key] || [];
      const signal = playing
        ? mappingSignal(mapping, smoothed, speechCue.bones[key], global.textInfluence, 1)
        : 0;
      const factor = playing ? (mapping.lerp ?? global.smoothing ?? 0.14) : 0.28;

      targets.forEach(target => {
        target.smoothedIntensity = lerp(target.smoothedIntensity || 0, signal, factor);
        const si = target.smoothedIntensity * (mapping.intensity ?? 1);
        const bone = target.bone;

        if (mapping.maxRotX !== undefined) bone.rotation.x = target.baseRotX + si * mapping.maxRotX;
        if (mapping.maxRotY !== undefined) bone.rotation.y = target.baseRotY + si * mapping.maxRotY;
        if (mapping.maxRotZ !== undefined) bone.rotation.z = target.baseRotZ + si * mapping.maxRotZ;
        if (mapping.maxPosX !== undefined) bone.position.x = target.basePosX + si * mapping.maxPosX;
        if (mapping.maxPosY !== undefined) bone.position.y = target.basePosY + si * mapping.maxPosY;
        if (mapping.maxPosZ !== undefined) bone.position.z = target.basePosZ + si * mapping.maxPosZ;
      });
    });
  });

  return null;
}

export default LipSyncController;
